import { useContext, type JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { FilteredClearancesFilterSummary } from './FilteredClearancesFilterSummary.tsx';
import { FilteredClearancesList } from './FilteredClearancesList.tsx';
import * as FilteredClearancesState from '../../state/FilteredClearancesState.ts';
import { FilteredClearancesFilterHalfSlideIn } from './FilteredClearancesFilterHalfSlideIn.tsx';
import { PermitTypeFilterHalfSlideIn } from './PermitTypeFilter.tsx';
import { IssueDateFilterHalfSlideIn } from './IssueDateFilter.tsx';
import { OperatorLoginFilterHalfSlideIn } from './OperatorLoginFilter.tsx';
import * as Http from '../../../api/Http.ts';
import { thenElse, isDefined } from 'dg-web-shared/lib/MaybeV2.ts';
import { PermitFilterHalfSlideIn } from './PermitFilter.tsx';
import * as CurrentOperatorLoginState from '../../../common/state/CurrentOperatorLoginState.ts';
import { AddMenu } from '../shared/AddMenu.tsx';
import {
    ButtonDropdown,
    ButtonDropdownLinkItem,
} from '../../../ui/slidein/ButtonDropdown.tsx';
import {
    FilterLayoutBody,
    FilterLayout,
    FilterLayoutHeader,
} from '../../../ui/filter/FilterLayout.tsx';
import { ValidDateFilterHalfSlideIn } from './ValidDateFilter.tsx';
import {
    captureText,
    exportText,
} from '../filtered-permits/FilteredPermits.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { urlWithOperatorExternalId } from '../../../utils/Utils.ts';
import { OperatorContext } from '../../../app/components/BaseLayoutAndData.tsx';

export const renderClearanceFilterSlideIns = (): JSX.Element[] => [
    <FilteredClearancesFilterHalfSlideIn
        key={'FilteredClearancesFilterSlideIns'}
    />,
    <PermitTypeFilterHalfSlideIn key={'FilteredClearancesPermitTypeFilter'} />,
    <OperatorLoginFilterHalfSlideIn
        key={'FilteredClearancesOperatorLogFilter'}
    />,
    <IssueDateFilterHalfSlideIn key={'FilteredClearancesIssueDateFilter'} />,
    <ValidDateFilterHalfSlideIn key={'FilteredClearancesValidDateFilter'} />,
    <PermitFilterHalfSlideIn key={'FilteredClearancesPermitFilter'} />,
];

const stringifyParams = (params: {
    [idx: string]: string | string | null;
}): string => {
    return Object.keys(params)
        .filter(k => isDefined(params[k]))
        .map((k: string) => `${k}=${params[k]}`)
        .join('&');
};

const filterToParams = (state: FilteredClearancesState): string => {
    return stringifyParams(
        Http.OperatorAccount.Clearances.FilteredList.getQueryParams({
            permitType: state.filter.permitTypes,
            issuedBy: thenElse(
                state.filter.operatorLogins,
                l =>
                    l.map(id =>
                        id ===
                        FilteredClearancesState.Filter
                            .OperatorLoginFilterOnlineOption
                            ? null
                            : id,
                    ),
                null,
            ),
            issueDateFrom: state.filter.issueDateFrom,
            issueDateTo: state.filter.issueDateTo,
            validDateFrom: state.filter.validDateFrom,
            validDateTo: state.filter.validDateTo,
            sortField: 'created',
            withOrWithoutPermits: state.filter.permits,
        }),
    );
};

interface FilteredClearancesState {
    filter: FilteredClearancesState.Filter.State;
    operatorLogin: CurrentOperatorLoginState.State;
}
export function FilteredClearances() {
    const { operatorExternalId } = useContext(OperatorContext);
    const { storeState } = useStore(store => ({
        filter: FilteredClearancesState.Filter.get(store),
        operatorLogin: CurrentOperatorLoginState.get(store),
    }));

    const filterActive = FilteredClearancesState.Filter.filterActive(
        storeState.filter,
    );
    const params = filterToParams(storeState);
    return (
        <FilterLayout>
            <FilterLayoutHeader
                filterSummary={<FilteredClearancesFilterSummary />}
                rightDropdown={<AddMenu label={captureText} />}
                secondRightDropdown={
                    <ButtonDropdown label={exportText}>
                        <ButtonDropdownLinkItem
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/clearances` +
                                    `/licenseplate-badge/filter/csv-export?${params}`,
                                operatorExternalId,
                            )}
                            label={
                                <Localized
                                    de="Freigaben"
                                    fr="Approbations"
                                    it="Approvazioni"
                                    en="Approvals"
                                />
                            }
                            disabled={!filterActive}
                        />
                    </ButtonDropdown>
                }
            />
            <FilterLayoutBody>
                <FilteredClearancesList />
            </FilterLayoutBody>
        </FilterLayout>
    );
}
